import {useEffect} from 'react';
import Box from '@mui/material/Box';
import {Button, Stack, Typography, useMediaQuery} from '@mui/material';
import theme from '$modules/booking/theme';
import EyeOpLogo from '$assets/svgs/eye-op.svg?react';
import CloseCircleIcon from '$assets/svgs/close-circle.svg?react';
import summaryVideo from '$assets/images/thankyou-booking.jpg';
import {AppSummaryViewStyles as styles} from '../styles';
import {BookingView, AppSummaryViewChoices, BookingDataUpdater} from '../types';
// import {useDeleteAppointment} from '../hooks';
import {dateToDDMMYYYY} from '$utils/date';

import {Color} from '$constants/style';
import {handleBeforeUnload} from '../utils';
import useHospitalInfo from '../../../hooks/useHospitalInfo';
import PaymentSummaryBox from '../components/PaymentSummaryBox';
import {getHospitalName} from '~/utils/helper';

interface AppSummaryViewProps {
  bookingChoices: AppSummaryViewChoices;
  setActiveView: (view: BookingView) => void;
  setBookingData: BookingDataUpdater;
}

function AppSummaryView(props: AppSummaryViewProps) {
  const {bookingChoices, setActiveView} = props;

  const {hospitalInfo} = useHospitalInfo();

  // const {requestDeleteAppointment, disableSubmit} =
  //   useDeleteAppointment(bookingChoices);

  const dateString = dateToDDMMYYYY(bookingChoices.date);
  const consultantName = bookingChoices.consultant.fullName;

  let preferredContactString =
    bookingChoices.patientContactDetail.patient.preferredContactMethod;
  if (preferredContactString === 'Both') {
    preferredContactString = 'email and SMS';
  }

  const navigateToCancelConfirmation = () => {
    setActiveView(BookingView.CancelConfirmationView);
  };

  const navigateToHospital = () => {
    if (hospitalInfo?.hospitalURL) {
      window.location.replace(hospitalInfo.hospitalURL);
    }
  };

  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return (
    <Box sx={styles.screenSection}>
      <Box sx={styles.bookingWrapper} flexDirection="column">
        <Box sx={styles.appSummaryWrap} flexDirection="column">
          <Box sx={styles.header}>
            <Box display="flex" gap={1} alignItems="center">
              <EyeOpLogo className="site-logo" />
              {isLarge && <div className="h-3 w-px bg-[#EBC3FF]" />}
              {isLarge && hospitalInfo?.textLogoURL && (
                <img
                  src={hospitalInfo.textLogoURL}
                  className="h-24 w-24"
                  alt="Hospital Text Logo"
                />
              )}
            </Box>
            <Box sx={styles.hospitalLogoWrap}>
              {hospitalInfo?.mobileLogoURL && (
                <img
                  src={hospitalInfo.mobileLogoURL}
                  className="hosptial-mobile-logo"
                  alt="Hospital Mobile Logo"
                />
              )}
            </Box>
          </Box>
          <Box sx={styles.appSummarySection}>
            <Stack spacing={1}>
              <Typography variant="h1" sx={styles.summaryHead}>
                Thank you for your booking!
              </Typography>
              <Typography variant="caption">
                Your appointment request with {consultantName} at{' '}
                {getHospitalName()} Hospital is confirmed. Appointment details
                will be sent via {preferredContactString}. Thank you for
                choosing us. For further assistance, please contact our support
                team.
              </Typography>
            </Stack>
            <Stack sx={styles.summaryActionBtnWrap}>
              {hospitalInfo?.hospitalURL && (
                <Button
                  variant="contained"
                  color="warning"
                  className="!bg-[#EBA300]"
                  sx={styles.bookingActionBtn}
                  onClick={navigateToHospital}
                >
                  <Typography variant="body1" color={Color.White}>
                    Discover more about {getHospitalName()}
                  </Typography>
                </Button>
              )}
            </Stack>
            <Stack spacing={2}>
              <Typography sx={styles.summaryTitle} variant="h3">
                Your appointment summary:
              </Typography>
              <Box>
                {(() => {
                  const isSelfPaying =
                    bookingChoices.patientInsuranceDetail?.selfPaying;

                  const totalAmount =
                    bookingChoices.consultant?.priceLists?.consultationFee || 0;

                  const consultationLabel = bookingChoices.care?.title
                    ? `${bookingChoices.care.title} `
                    : 'Consultation';

                  const headerText = isSelfPaying
                    ? 'Total paid:'
                    : 'Insurance claim:';

                  return (
                    <PaymentSummaryBox
                      totalAmount={totalAmount}
                      items={[
                        {
                          label: consultationLabel,
                          amount: totalAmount,
                        },
                      ]}
                      currency="£"
                      headerText={headerText}
                    />
                  );
                })()}
              </Box>
              <Stack spacing={2}>
                <Stack spacing={1}>
                  <Typography
                    variant="body1"
                    sx={{color: theme.palette.primary.main}}
                  >
                    Appointment
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{color: theme.palette.text.secondary}}
                  >
                    {bookingChoices.care.title}
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography
                    variant="body1"
                    sx={{color: theme.palette.primary.main}}
                  >
                    Consultant
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{color: theme.palette.text.secondary}}
                  >
                    {consultantName}
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography
                    variant="body1"
                    sx={{color: theme.palette.primary.main}}
                  >
                    Date
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{color: theme.palette.text.secondary}}
                  >
                    {dateString}
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography
                    variant="body1"
                    sx={{color: theme.palette.primary.main}}
                  >
                    Time
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{color: theme.palette.text.secondary}}
                  >
                    To be sent via {preferredContactString}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <Stack sx={styles.summaryActionBtnWrap}>
              <Button
                variant="contained"
                color="secondary"
                className="delete-button"
                sx={styles.bookingActionBtn}
                startIcon={<CloseCircleIcon />}
                onClick={navigateToCancelConfirmation}
              >
                <Typography variant="body1" color={theme.palette.text.primary}>
                  Delete your booking
                </Typography>
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.appSummaryVideoSection}>
        <img src={summaryVideo} alt="video" />
      </Box>
    </Box>
  );
}

export default AppSummaryView;
